export interface Driver {
  id: string
  accountNo: string
  name: string
  jobTitle: string
  phoneNo: string
  email: string
  department: string
  hasConfiguratorAccess: boolean
  regNo: string
  webUserType: string
  isWebUser: boolean
  type: string
  city?: string
  postCode?: string
  contactType?: string
}

export type CarUsage = {
  accountNo?: string
  carId?: string
  eventType?: string
  [key: string]: unknown
}

export interface Employees {
  name: string
  jobTitle: string
  phoneNo: string
  email: string
  department: string
  hasConfiguratorAccess: boolean
  regNo: string
  webUserType: string
}

export interface Organisation {
  id: string
  name: string
  vatNr?: string
  parentId?: string
}

export interface UserOrganisations {
  defaultOrganisationId: string | null
  organisations: Organisation[]
}

export interface ConsolidateInvoice {
  code: string
  description: string
}

export interface UserConsolidateInvoices {
  defaultConsolidateInvoiceCode: string | null
  hasAccessToAll: boolean
  consolidateInvoices: ConsolidateInvoice[]
}

export interface CompanyContext {
  company: Organisation | null
  consolidateInvoice: ConsolidateInvoice | null
}

export interface FleetSummary {
  totalCount: number
  orderedCount: number
  activeCount: number
  expiringCount: number
  closedCount: number
  unresolvedCount: number
  administratedCount: number
  overMileageCount: number
}

export const FLEET_COMPANY_ID = '305496'

export const FLEET_FILTERS = [
  'all',
  'order',
  'active',
  'administrated',
  'expiring',
  'closed',
  'unresolved',
  'overmileage',
] as const

export type FleetFilter = (typeof FLEET_FILTERS)[number]

export const FLEET_DEFAULT_FILTER: FleetFilter = 'active'

export const MAX_MESSAGE_LENGTH = 200

export const EMPLOYEE_ACCESS_TYPES = ['YES', 'NO', 'OWN_CAR_ONLY'] as const

export type EmployeeAccessType = (typeof EMPLOYEE_ACCESS_TYPES)[number]

export function toFleetFilter(value?: string | null): FleetFilter {
  return FLEET_FILTERS.includes(value as FleetFilter) ? (value as FleetFilter) : FLEET_DEFAULT_FILTER
}

export function fleetFilterCount(summary: FleetSummary | null | undefined, filter: FleetFilter) {
  if (!summary) return undefined

  switch (filter) {
    case 'order':
      return summary.orderedCount
    case 'active':
      return summary.activeCount
    case 'administrated':
      return summary.administratedCount
    case 'expiring':
      return summary.expiringCount
    case 'closed':
      return summary.closedCount
    case 'unresolved':
      return summary.unresolvedCount
    case 'overmileage':
      return summary.overMileageCount
    default:
      return summary.totalCount
  }
}

export type VehicleFilters = {
  status?: string | string[]
  contactPerson?: string | string[]
  invoiceGroup?: string | string[]
  vehicleMake?: string | string[]
  vehicleType?: string | string[]
  fuelType?: string | string[]
  consolidateInvoiceCode?: string | string[]
  deviationCodes?: string[]
}

export const EXPENSE_IDS = [
  'service',
  'tireStorage',
  'serviceAndMaintenance',
  'fuel',
  'toll',
  'workshop',
  'park',
] as const

export type ExpenseId = (typeof EXPENSE_IDS)[number]

export function isExpenseId(value?: string): value is ExpenseId {
  return EXPENSE_IDS.includes(value as ExpenseId)
}

export type Expense = {
  date: string
  code?: string
  description?: string
  station?: string
  operator?: string
  price?: number
  volume?: number
  pricePerLiter?: number
  dueDate?: string
}

export type InsurancePhoneType = 'road_assistant' | 'claim' | 'car_registration'

export type FleetInsurance = {
  code?: string
  policyNo?: string
  homepage?: string
  email?: string
  phoneNumbers?: { type: InsurancePhoneType; number: string }[]
}

export type UserTaskSummary = {
  readyForSigning: number
  sentForSigning: number
  selections: number
}

export const FLEET_PORTAL_ROLES = {
  ADMIN: 'comm-kc-admin',
  AUTOPLAN_PURCHASING: 'comm-kc-ap-purchasing',
  AUTOPLAN_KAM: 'comm-kc-ap-kam',
  AUTOPLAN_CUSTOMER_SERVICE: 'comm-kc-ap-cs',
  AUTOPLAN_MANAGEMENT: 'comm-kc-ap-mgmt',
  AUTOPLAN_TECHNICAL_ADMIN: 'comm-kc-ap-teknisk',
  CAR_SELLER: 'comm-kc-carseller',
  FLEET_ADMIN: 'comm-kc-fleet-admin',
  FLEET_USER: 'comm-kc-fleet-user',
} as const

export type FleetPortalRole = (typeof FLEET_PORTAL_ROLES)[keyof typeof FLEET_PORTAL_ROLES]

export const WEB_MODULES = {
  HOME: 'home',
  COMPANY: 'company',
  CAR_PARK: 'car-park',
  REPORTS: 'reports',
  EMPLOYEE_LIST: 'employee-list',
  ARCHIVE: 'archive',
  TASKS: 'tasks',
} as const

export type WebModule = (typeof WEB_MODULES)[keyof typeof WEB_MODULES]

const ALL_FLEET_PORTAL_ROLES = Object.values(FLEET_PORTAL_ROLES)

const FLEET_MANAGEMENT_ROLES = [
  FLEET_PORTAL_ROLES.ADMIN,
  FLEET_PORTAL_ROLES.FLEET_ADMIN,
  FLEET_PORTAL_ROLES.AUTOPLAN_KAM,
  FLEET_PORTAL_ROLES.AUTOPLAN_MANAGEMENT,
]

export const WEB_MODULE_PERMISSIONS: Record<WebModule, FleetPortalRole[]> = {
  [WEB_MODULES.HOME]: ALL_FLEET_PORTAL_ROLES,
  [WEB_MODULES.COMPANY]: ALL_FLEET_PORTAL_ROLES,
  [WEB_MODULES.CAR_PARK]: FLEET_MANAGEMENT_ROLES,
  [WEB_MODULES.REPORTS]: FLEET_MANAGEMENT_ROLES,
  [WEB_MODULES.EMPLOYEE_LIST]: FLEET_MANAGEMENT_ROLES,
  [WEB_MODULES.ARCHIVE]: FLEET_MANAGEMENT_ROLES,
  [WEB_MODULES.TASKS]: FLEET_MANAGEMENT_ROLES,
}
